/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for the Notifications feature.
 *
 * Covers: preferences, inbox (in-app), unread count, templates.
 * Server state from /notifications/* endpoints.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authenticatedApi } from "@/api/authenticatedClient";

// ---------------------------------------------------------------------------
// Types (match backend schemas)
// ---------------------------------------------------------------------------

export interface PreferenceItem {
  id?: string;
  event_type: string;
  app_enabled: boolean;
  email_enabled: boolean;
  teams_enabled: boolean;
  min_severity?: string | null;
}

interface PreferenceListResponse {
  items: PreferenceItem[];
  total: number;
}

export interface NotificationLogItem {
  id: string;
  user_id: string | null;
  event_type: string;
  channel: string;
  entity_type: string | null;
  entity_id: string | null;
  event_data: Record<string, string> | null;
  sent_at: string;
  is_read: boolean;
  read_at: string | null;
}

interface NotificationLogResponse {
  items: NotificationLogItem[];
  total: number;
  next_cursor: string | null;
}

interface InboxCountResponse {
  unread: number;
}

export interface TemplateItem {
  id: string;
  template_code: string;
  event_type: string;
  channel: string;
  subject_template: string | null;
  body_template: string;
  available_variables: Array<{ name: string; type: string }> | null;
  is_active: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const NOTIFICATION_KEYS = {
  all: ["notifications"] as const,
  preferences: () => [...NOTIFICATION_KEYS.all, "preferences"] as const,
  inbox: (unreadOnly?: boolean, limit?: number) =>
    [...NOTIFICATION_KEYS.all, "inbox", { unreadOnly, limit }] as const,
  // Prefix that matches EVERY inbox list variant (any {unreadOnly, limit}) AND the count key
  // below, since both start with ["notifications","inbox"]. Invalidating this after a read
  // action refetches the list and the badge together — a plain `inbox()` filter carries
  // {unreadOnly: undefined, limit: undefined}, which does NOT partial-match a live
  // {unreadOnly:false, limit:20} key, so the list would never refetch (the stale-dot bug).
  inboxRoot: () => [...NOTIFICATION_KEYS.all, "inbox"] as const,
  inboxCount: () => [...NOTIFICATION_KEYS.all, "inbox", "count"] as const,
  templates: (eventType?: string) =>
    [...NOTIFICATION_KEYS.all, "templates", { eventType }] as const,
  template: (eventType: string, channel: string) =>
    [...NOTIFICATION_KEYS.all, "templates", eventType, channel] as const,
};

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

async function fetchPreferences(): Promise<PreferenceListResponse> {
  return authenticatedApi.get<PreferenceListResponse>("/notifications/preferences");
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.preferences(),
    queryFn: fetchPreferences,
    staleTime: 5 * 60 * 1000,
  });
}

export function useNotificationPreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: PreferenceItem[]) =>
      authenticatedApi.put<PreferenceListResponse>("/notifications/preferences", { items }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.preferences() });
    },
  });
}

// ---------------------------------------------------------------------------
// Inbox (in-app notifications)
// ---------------------------------------------------------------------------

async function fetchInbox(unreadOnly: boolean, limit: number): Promise<NotificationLogResponse> {
  const params = new URLSearchParams();
  if (unreadOnly) params.set("unread_only", "true");
  params.set("limit", String(limit));
  return authenticatedApi.get<NotificationLogResponse>(`/notifications/inbox?${params.toString()}`);
}

export function useNotificationInbox(unreadOnly = false, limit = 50) {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.inbox(unreadOnly, limit),
    queryFn: () => fetchInbox(unreadOnly, limit),
    staleTime: 30 * 1000, // 30s — inbox is near-realtime
    // Poll so the drawer list tracks the badge count (which already polls every 60s), instead of
    // the list going stale until remount (docs/specs/portal-data-freshness.md).
    refetchInterval: 60 * 1000,
  });
}

async function fetchInboxCount(): Promise<InboxCountResponse> {
  return authenticatedApi.get<InboxCountResponse>("/notifications/inbox/count");
}

export function useNotificationInboxCount() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.inboxCount(),
    queryFn: fetchInboxCount,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Poll every 60s for badge updates
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      authenticatedApi.put<{ success: boolean }>(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      // Prefix invalidation refetches every inbox list variant AND the count (fixes the
      // stale unread-dot that previously lingered until a full page refresh).
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.inboxRoot() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authenticatedApi.put<{ marked_read: number }>("/notifications/read-all"),
    onSuccess: () => {
      // Prefix invalidation refetches every inbox list variant AND the count in one pass.
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.inboxRoot() });
    },
  });
}

// ---------------------------------------------------------------------------
// Templates (admin)
// ---------------------------------------------------------------------------

async function fetchTemplates(eventType?: string): Promise<TemplateItem[]> {
  const params = eventType ? `?event_type=${eventType}` : "";
  return authenticatedApi.get<TemplateItem[]>(`/notifications/templates${params}`);
}

export function useNotificationTemplates(eventType?: string) {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.templates(eventType),
    queryFn: () => fetchTemplates(eventType),
    staleTime: 5 * 60 * 1000,
  });
}

export function useNotificationTemplate(eventType: string, channel: string) {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.template(eventType, channel),
    queryFn: () =>
      authenticatedApi.get<TemplateItem>(`/notifications/templates/${eventType}/${channel}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateNotificationTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventType,
      channel,
      body,
    }: {
      eventType: string;
      channel: string;
      body: { subject_template?: string | null; body_template: string; is_active?: boolean };
    }) =>
      authenticatedApi.put<TemplateItem>(`/notifications/templates/${eventType}/${channel}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.templates() });
    },
  });
}
